import type { FilterSearch } from 'noddi-util';
import type { QueryParamConfig } from 'use-query-params';

/**
 * A filter's value as the URL holds it. Three states, because a list with a default needs them all:
 * - `undefined` -- not chosen (key absent): the list's default applies;
 * - `null` -- cleared (key present, empty): everything, no param sent;
 * - a value -- chosen.
 * A plain `withDefault(ArrayParam, defaults)` folds cleared into not chosen, so clearing or excluding the last
 * default brought it back and sent one value as both included and excluded.
 */
export type Stored<T> = T | null | undefined;

/** Include + exclude of one multi-select. An excluded value is never included. */
export type SelectionState<T> = {
  include: Stored<T[]>;
  exclude: T[];
};

/** What the request filters on: the default when nothing is chosen, never an excluded value. */
export const includedValues = <T>({ include, exclude }: SelectionState<T>, defaults: readonly T[]): T[] =>
  (include === undefined ? defaults : (include ?? [])).filter((value) => !exclude.includes(value));

/** Empty means everything; with a default, that must be stored as cleared or the default comes back. */
export const storedSelection = <T>(values: readonly T[], hasDefaults: boolean): Stored<T[]> =>
  values.length > 0 ? [...values] : hasDefaults ? null : undefined;

/** The user picked `values` to include: none of them stays excluded. */
const withIncluded = <T>(state: SelectionState<T>, values: readonly T[], hasDefaults: boolean) => ({
  include: storedSelection(values, hasDefaults),
  exclude: state.exclude.filter((value) => !values.includes(value))
});

/** The user picked `values` to exclude: none of them stays included. Not chosen keeps following the default. */
const withExcluded = <T>(state: SelectionState<T>, values: readonly T[], hasDefaults: boolean) => ({
  include: Array.isArray(state.include)
    ? storedSelection(
        state.include.filter((value) => !values.includes(value)),
        hasDefaults
      )
    : state.include,
  exclude: [...values]
});

/** Encoded value of the cleared state: `?statuses=`. */
const CLEARED = '';

type RawParam = string | (string | null)[] | null | undefined;

const rawValues = (raw: RawParam) => (Array.isArray(raw) ? raw : [raw]).filter((value) => value != null);

/**
 * URL param for a selection with a default. Values `parse` rejects (a renamed enum member in an old link)
 * are dropped; a link left with none of its values opens on the default.
 */
export const selectionParam = <T extends string | number>(
  parse: (value: string) => T | undefined
): QueryParamConfig<Stored<T[]>, Stored<T[]>> => ({
  encode: (values) => (values === undefined ? undefined : values === null ? CLEARED : values.map(String)),
  decode: (raw) => {
    if (raw === undefined) {
      return undefined;
    }
    const values = rawValues(raw);
    if (values.every((value) => value === CLEARED)) {
      return null;
    }
    const parsed = values.flatMap((value) => {
      const parsedValue = value === CLEARED ? undefined : parse(value);
      return parsedValue === undefined ? [] : [parsedValue];
    });
    return parsed.length > 0 ? parsed : undefined;
  }
});

/** Excluded values: no default, so absent and empty both mean none (`parseNumber('')` would read id 0). */
export const excludedParam = <T extends string | number>(
  parse: (value: string) => T | undefined
): QueryParamConfig<T[] | undefined, T[]> => ({
  encode: (values) => (values && values.length > 0 ? values.map(String) : undefined),
  decode: (raw) =>
    rawValues(raw).flatMap((value) => {
      const parsedValue = value === CLEARED ? undefined : parse(value);
      return parsedValue === undefined ? [] : [parsedValue];
    })
});

/** A single value (boolean, date) with the same three states as a selection. */
export const storedParam = <T>(
  encodeValue: (value: T) => string,
  parse: (value: string) => T | undefined
): QueryParamConfig<Stored<T>, Stored<T>> => ({
  encode: (value) => (value === undefined ? undefined : value === null ? CLEARED : encodeValue(value)),
  decode: (raw) => {
    if (raw === undefined) {
      return undefined;
    }
    const [value] = rawValues(raw);
    return value === undefined || value === CLEARED ? null : parse(value);
  }
});

/** The value the request filters on: the default when not chosen, nothing when cleared. */
export const storedValue = <T>(stored: Stored<T>, defaultValue: T | undefined): T | undefined =>
  stored === undefined ? defaultValue : (stored ?? undefined);

/** Empty value with a default is stored as cleared; without one, absent keeps the URL clean. */
export const toStored = <T>(value: T | null | undefined, hasDefault: boolean): Stored<T> =>
  value != null ? value : hasDefault ? null : undefined;

export const parseNumber = (value: string) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
};

/** URL key for an API param: `excludedStatuses` → `excluded-statuses`. */
export const toUrlKey = (param: string) => param.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);

const BOOLEAN_PARAM = storedParam<boolean>(
  (value) => (value ? '1' : '0'),
  (value) => (value === '1' || value === 'true' ? true : value === '0' || value === 'false' ? false : undefined)
);

/**
 * The builders' URL state, for `unit.compose`: params with the three states (absent = default, empty = cleared,
 * values) and the include/exclude transitions that keep an excluded value out of the selection.
 */
export const filterUrlState = {
  urlKey: toUrlKey,
  /** `statuses` → `excludedStatuses`, the nsdk exclusion param a builder writes. */
  excludedKey: (key: string) => `excluded${key.charAt(0).toUpperCase()}${key.slice(1)}`,
  selection: selectionParam,
  excluded: excludedParam,
  stored: storedParam,
  boolean: BOOLEAN_PARAM,
  parseNumber,
  includedValues,
  withIncluded,
  withExcluded,
  storedValue,
  toStored
};

declare const filterLinkBrand: unique symbol;

/**
 * The params a link into one list may set: what its filters read from the URL, which after `map` is not the
 * endpoint's query. Only `FilterLinkOf` makes one, from the list's filters hook, so an nsdk query type is refused.
 */
export type FilterLink<TParams> = TParams & { readonly [filterLinkBrand]: true };

type FilterLinkKeys<TLink> = Exclude<keyof TLink, typeof filterLinkBrand>;

/**
 * What a link sets. `null` or `[]` clears a filter: the list's default does not apply (absent = default). A link
 * type with no keys (none given, a prefixed spec, a composite on a URL-param record) is `never`, not `{}`, which
 * would take any key.
 */
export type FilterLinkParams<TLink> = [FilterLinkKeys<TLink>] extends [never]
  ? never
  : { [K in FilterLinkKeys<TLink>]?: TLink[K] | null };

/**
 * A link into a filtered list (`partnerRoutes.bookings.getPath({ filters: filterSearch<PartnerBookingListLink>({
 * statusIds: [4] }) })` → `/bookings?status-ids=4`), on the keys its filters read: a key the list does not read is a
 * type error. Values as the control holds them: dates as
 * API dates, money in kroner (the builder converts to øre), booleans as `1` / `0`.
 */
export const filterSearch = <TLink extends FilterLink<object>>(params: FilterLinkParams<TLink>): FilterSearch => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    const urlKey = toUrlKey(key);
    if (value === null || (Array.isArray(value) && value.length === 0)) {
      search.append(urlKey, CLEARED);
      continue;
    }
    for (const item of Array.isArray(value) ? value : [value]) {
      if (item !== undefined && item !== null) {
        search.append(urlKey, typeof item === 'boolean' ? (item ? '1' : '0') : String(item));
      }
    }
  }
  return search.toString() as FilterSearch;
};
